"use client";

import { useTranslations } from "next-intl";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { BarChart3, Bell, MessageCircle, Star, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getAvatarUrl } from "@/lib/getAvatarUrl";
import { SQUARE_LG, imagePresetUrl } from "@/lib/images/presets";
import { UnifiedFollowButton } from "@/components/UnifiedFollowButton";
import { cn } from "@/lib/utils";
import { ProfileActionsMenu } from "@/components/profile-page/profile-actions-menu";
import { ProfileStatInline, ProfileStatSeparator, ProfileRatingStatSkeleton } from "@/components/Profile/profile-stat-inline";
import {
  ProfileReviewScoreLink,
  profileStatLineClassName,
} from "@/components/reviews/profile-review-score-link";
import { getProfileAvatarScale } from "@/lib/profile-scroll-effects";
import { countryNameToFlagEmoji } from "@/lib/profile-utils";

type ProfileHeroProps = {
  displayName: string;
  handle: string;
  country: string;
  bio: string;
  avatarUrl: string | null;
  listingsCount: number;
  salesCount: number;
  followersCount: number;
  isOwner: boolean;
  isFollowing: boolean;
  loading?: boolean;
  currentUserId?: string | null;
  profileId: string;
  onFollowClick: (following: boolean) => void;
  onMessageClick?: () => void;
  onNotificationClick?: () => void;
  isSubscribed?: boolean;
  isLoadingSubscription?: boolean;
  hideFollowButton?: boolean;
  avatarOverlapping?: boolean;
  scrollY?: number;
  avatarScaleOnScroll?: boolean;
  onEditClick?: () => void;
};

export function ProfileHero({
  displayName,
  handle,
  country,
  bio,
  avatarUrl,
  listingsCount,
  salesCount,
  followersCount,
  isOwner,
  isFollowing,
  loading = false,
  currentUserId,
  profileId,
  onFollowClick,
  onMessageClick,
  onNotificationClick,
  isSubscribed = false,
  isLoadingSubscription = false,
  hideFollowButton = false,
  avatarOverlapping = false,
  scrollY = 0,
  avatarScaleOnScroll = false,
  onEditClick,
}: ProfileHeroProps) {
  const t = useTranslations("profile");
  const tInfo = useTranslations("profile.info");
  const reduceMotion = useReducedMotion();
  const resolvedAvatar = getAvatarUrl(avatarUrl);
  const bioText = (bio ?? "").trim();
  const countryFlag = countryNameToFlagEmoji(country);

  const avatarNode = (
    <div
      className={cn(
        "relative overflow-hidden rounded-full border-[3px] border-background shadow-sm",
        // Solid white behind uploaded avatars (both themes) so transparent
        // PNGs composite on white instead of the theme surface.
        resolvedAvatar ? "bg-white" : "bg-muted",
        avatarOverlapping ? "h-20 w-20" : "h-24 w-24 border-2 border-border/50",
      )}
    >
      {resolvedAvatar ? (
        <Image
          // `h-20`/`h-24` = 80/96 CSS px -> SQUARE_LG. Sized here, not at the
          // `getAvatarUrl` call: the same variable is handed to
          // `UnifiedFollowButton`, which forwards it into a notification payload.
          src={imagePresetUrl(resolvedAvatar, SQUARE_LG)}
          alt={displayName}
          fill
          className="object-cover"
          sizes={avatarOverlapping ? "80px" : "96px"}
          priority
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <User className={cn("text-muted-foreground", avatarOverlapping ? "h-8 w-8" : "h-10 w-10")} />
        </div>
      )}
    </div>
  );

  const visitorActions = !isOwner ? (
    <div className="flex shrink-0 items-center gap-1.5">
      {!hideFollowButton ? (
        <UnifiedFollowButton
          currentUserId={currentUserId ?? null}
          profileId={profileId}
          initialFollowing={isFollowing}
          variant="profile-stats"
          context="profile-page"
          onFollowChange={onFollowClick}
          disabled={loading}
          profileUsername={handle}
          profileAvatarUrl={resolvedAvatar ?? null}
          // The container owns the post-follow notification prompt and the
          // bell's isSubscribed state — let it drive both from one place so the
          // revealed bell can't desync from a subscription made here.
          promptNotificationsOnFollow={false}
          className="h-8 rounded-full px-3.5 text-xs font-semibold"
        />
      ) : null}
      {onMessageClick ? (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 rounded-full border-border"
          onClick={onMessageClick}
          aria-label={t("message")}
        >
          <MessageCircle className="h-4 w-4" />
        </Button>
      ) : null}
      {/* The bell is a sub-feature of following — it only exists once you
          follow (initial={false} so it animates on the follow *action*, not
          when loading an already-followed profile). Scale-in + a short ring
          swing reveal it in place; reduced-motion collapses to a fade. */}
      <AnimatePresence initial={false}>
        {onNotificationClick && isFollowing ? (
          <motion.div
            key="profile-notification-bell"
            className="inline-flex"
            initial={reduceMotion ? { opacity: 0 } : { scale: 0.3, opacity: 0 }}
            animate={
              reduceMotion
                ? { opacity: 1 }
                : { scale: 1, opacity: 1, rotate: [0, -16, 12, -8, 5, 0] }
            }
            exit={reduceMotion ? { opacity: 0 } : { scale: 0.3, opacity: 0 }}
            transition={{
              default: { type: "spring", stiffness: 520, damping: 24 },
              rotate: { duration: 0.55, ease: "easeOut", delay: 0.06 },
              opacity: { duration: 0.18 },
            }}
          >
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                "h-8 w-8 rounded-full border border-border",
                isSubscribed && "bg-primary text-primary-foreground border-transparent",
                isLoadingSubscription && "animate-pulse",
              )}
              disabled={isLoadingSubscription}
              onClick={onNotificationClick}
              aria-label={isSubscribed ? tInfo("notificationsOn") : tInfo("getNotifications")}
            >
              <Bell className={cn("h-4 w-4", isSubscribed && "fill-current")} />
            </Button>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  ) : (
    <div className="flex shrink-0 items-center gap-1.5">
      {onEditClick ? (
        <Button
          type="button"
          className="h-7 rounded-full px-3 text-xs font-medium md:hidden"
          onClick={onEditClick}
        >
          {t("editProfile")}
        </Button>
      ) : null}
      <Button
        asChild
        className={cn(
          "h-7 rounded-full px-3 text-xs font-medium",
          onEditClick ? "hidden md:inline-flex" : "inline-flex",
        )}
      >
        <Link href="/profile/edit">{t("editProfile")}</Link>
      </Button>
      <Button
        asChild
        variant="outline"
        size="icon"
        className="h-7 w-7 rounded-full border-border"
      >
        <Link href="/analytics" aria-label={t("manageListingsA11y")}>
          <BarChart3 className="h-3.5 w-3.5" />
        </Link>
      </Button>
    </div>
  );

  return (
    <div className={cn("pb-4", avatarOverlapping && "relative pt-12")}>
      {avatarOverlapping ? (
        <div
          className="absolute -top-10 right-0 z-10"
          style={
            avatarScaleOnScroll
              ? {
                  transform: `scale(${getProfileAvatarScale(scrollY)})`,
                  transformOrigin: "bottom right",
                }
              : undefined
          }
        >
          {avatarNode}
        </div>
      ) : null}

      <h1 className="text-xl font-bold tracking-tight text-foreground line-clamp-2 pr-24">
        {displayName}
      </h1>

      <div className="mt-0.5 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
          <span className="truncate font-semibold text-muted-foreground">@{handle}</span>
          {country ? (
            <>
              <span aria-hidden>·</span>
              {countryFlag ? (
                <span
                  className="shrink-0 text-[15px] leading-none"
                  role="img"
                  aria-label={country}
                  title={country}
                >
                  {countryFlag}
                </span>
              ) : (
                <span className="truncate">{country}</span>
              )}
            </>
          ) : null}
        </div>
        {visitorActions}
      </div>

      {bioText ? (
        <p className="mt-2 text-sm leading-relaxed text-foreground/90 line-clamp-5">
          {bioText}
        </p>
      ) : null}

      <div className={cn("mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm", profileStatLineClassName)}>
        <ProfileStatInline stat="listings" value={listingsCount} />
        <ProfileStatSeparator />
        <ProfileStatInline stat="sales" value={salesCount} />
        <ProfileStatSeparator />
        <ProfileStatInline stat="followers" value={followersCount} />
        <ProfileReviewScoreLink
          profileId={profileId}
          tone="profile"
          isOwnProfile={isOwner}
          showSkeletonWhileLoading
          renderSkeleton={() => (
            <>
              <ProfileStatSeparator />
              <ProfileRatingStatSkeleton />
            </>
          )}
          renderTrigger={({ label, onClick, hasNumericRating }) => (
            <>
              <ProfileStatSeparator />
              <button
                type="button"
                onClick={onClick}
                className={cn(
                  profileStatLineClassName,
                  "inline-flex items-center gap-1 underline underline-offset-2 transition-opacity hover:opacity-80",
                )}
              >
                <Star
                  className={cn(
                    "h-3.5 w-3.5",
                    hasNumericRating && "fill-amber-500 text-amber-500",
                  )}
                />
                <span className="font-bold tabular-nums">{label}</span>
              </button>
            </>
          )}
        />
      </div>

      {!avatarOverlapping ? (
        <div className="mt-3 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1" />
          <div className="flex flex-col items-end gap-2">
            {!isOwner ? (
              <ProfileActionsMenu
                profileId={profileId}
                username={handle}
                avatarUrl={avatarUrl}
                currentUserId={currentUserId ?? undefined}
                triggerClassName="h-10 w-10 rounded-full inline-flex items-center justify-center hover:bg-muted/80"
                iconClassName="h-5 w-5"
                showShareInMenu={false}
              />
            ) : null}
            {avatarNode}
          </div>
        </div>
      ) : null}
    </div>
  );
}
